import React, { useState } from "react";
import { toast } from "sonner";
import { Home, Building2, Car, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Reveal } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { CTAButton } from "@/components/shared/CTAButton";
import { submitQuote, formatApiError } from "@/lib/api";

const MOVE_TYPES = [
  { value: "Home", label: "Home Relocation", icon: Home },
  { value: "Office", label: "Office / Commercial", icon: Building2 },
  { value: "Vehicle", label: "Vehicle Transport", icon: Car },
];

const empty = { name: "", phone: "", email: "", pickup_city: "", drop_city: "", move_date: "", move_type: "", message: "" };

const trustPoints = [
  "Free, no-obligation quote within hours",
  "Transparent pricing — no hidden charges",
  "Insured transit & professional packing",
];

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup_city || !form.drop_city || !form.move_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitQuote(form);
      toast.success(res.message || "Quote request received!");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="scroll-mt-24 bg-white py-24 lg:py-32" data-testid="quote-form-section">
      <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Request a quote"
            title="Get your free moving estimate."
            description="Tell us where you're headed and what you're moving — our team reviews every request and follows up quickly with a tailored quote."
          />
          <ul className="mt-8 space-y-4">
            {trustPoints.map((t) => (
              <li key={t} className="flex items-start gap-3 text-ink">
                <CheckCircle2 className="h-6 w-6 text-brand-accent shrink-0" />
                <span className="text-muted-foreground">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right form */}
        <Reveal direction="left" className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-gray-100 bg-surface p-6 sm:p-10 shadow-card"
            data-testid="quote-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="q-name">Full Name *</Label>
                <Input id="q-name" data-testid="quote-name" value={form.name} onChange={set("name")} placeholder="Your name" className="bg-white h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-phone">Phone Number *</Label>
                <Input id="q-phone" data-testid="quote-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="bg-white h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-pickup">Pickup City *</Label>
                <Input id="q-pickup" data-testid="quote-pickup" value={form.pickup_city} onChange={set("pickup_city")} placeholder="e.g. Gurgaon" className="bg-white h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-drop">Drop City *</Label>
                <Input id="q-drop" data-testid="quote-drop" value={form.drop_city} onChange={set("drop_city")} placeholder="e.g. Mumbai" className="bg-white h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-date">Move Date</Label>
                <Input id="q-date" type="date" data-testid="quote-date" value={form.move_date} onChange={set("move_date")} className="bg-white h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-type">Type of Move *</Label>
                <Select value={form.move_type} onValueChange={set("move_type")}>
                  <SelectTrigger id="q-type" data-testid="quote-type" className="bg-white h-12 rounded-xl">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MOVE_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value} data-testid={`quote-type-${m.value.toLowerCase()}`}>
                        <span className="flex items-center gap-2"><m.icon className="h-4 w-4" /> {m.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="q-email">Email (optional)</Label>
                <Input id="q-email" type="email" data-testid="quote-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="bg-white h-12 rounded-xl" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="q-message">Additional Details</Label>
                <Textarea id="q-message" data-testid="quote-message" value={form.message} onChange={set("message")} placeholder="Tell us about your items, floors, special handling..." className="bg-white rounded-xl min-h-[110px]" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-8 py-4 hover:bg-brand-deep transition-colors disabled:opacity-70 disabled:cursor-not-allowed shadow-card"
            >
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</> : "Request My Free Quote"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
